import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { format, addMonths, subMonths } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import GanttChart from '../../../components/gantt';

// Hooks
import { useTasks } from '../hooks/useTasks';
import { useTaskCategories } from '../hooks/useTaskCategories';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useAuth } from '../../../context/AuthContext';

// Utils
import { getPriorityColor } from '../utils/taskStateUtils';

import PageHeader from '../../../components/common/PageHeader';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';

export default function CalendarPage() {
  const navigate = useNavigate();
  const { showError } = useSnackbar();
  const { user } = useAuth();
  const { t } = useTranslation(['tasks']);

  // State
  const [currentDate] = useState(new Date());
  const [calendarTasks, setCalendarTasks] = useState([]);

  // API hooks
  const {
    loading,
    error,
    clearError,
    getCalendarTasks,
  } = useTasks();

  const { categories, fetchTaskCategories } = useTaskCategories();

  // Load calendar data with exact date range from chart
  const loadCalendarData = useCallback(async (startDate = null, endDate = null) => {
    try {
      const rangeStart = startDate || subMonths(currentDate, 1);
      const rangeEnd = endDate || addMonths(currentDate, 3);

      const params = {
        start_date: format(rangeStart, 'yyyy-MM-dd'),
        end_date: format(rangeEnd, 'yyyy-MM-dd'),
        staff_member_id: user?.staff_member_id
      };

      const result = await getCalendarTasks(params);
      if (result.success) {
        const newTasks = result.data?.data?.tasks || [];
        setCalendarTasks(prev => {
          const taskMap = new Map(prev.map(t => [t.task_id, t]));
          newTasks.forEach(t => taskMap.set(t.task_id, t));
          return Array.from(taskMap.values());
        });
      } else {
        showError(t('tasks:calendar.failed_to_load'));
      }
    } catch (error) {
      showError(t('tasks:calendar.failed_to_load'));
    }
  }, [currentDate, getCalendarTasks, showError, user?.staff_member_id, t]);

  // Function to handle when chart needs data for specific date range
  const handleDateRangeChange = useCallback((startDate, endDate) => {
    loadCalendarData(startDate, endDate);
  }, [loadCalendarData]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadCalendarData(),
        fetchTaskCategories({ 
          active_only: true
        }),
      ]);
    };
    loadData();
  }, [loadCalendarData, fetchTaskCategories]);

  // Transform tasks data for Gantt chart
  const ganttData = useMemo(() => {
    if (!calendarTasks.length) {
      return { categories: [], baseDate: new Date() };
    }

    // Group tasks by category
    const tasksByCategory = {};
    calendarTasks.forEach(task => {
      const categoryId = task.category_id || 'uncategorized';
      if (!tasksByCategory[categoryId]) {
        tasksByCategory[categoryId] = [];
      }
      tasksByCategory[categoryId].push(task);
    });

    // Create category mapping
    const categoryMap = {};
    categories.forEach(category => {
      categoryMap[category.category_id] = category;
    });

    // Add uncategorized category if needed
    if (tasksByCategory['uncategorized']) {
      categoryMap['uncategorized'] = {
        category_id: 'uncategorized',
        name: t('tasks:calendar.uncategorized'),
        color_code: '#9e9e9e'
      };
    }

    // Transform to Gantt format
    const ganttCategories = Object.entries(tasksByCategory).map(([categoryId, categoryTasks]) => {
      const category = categoryMap[categoryId];
      
      return {
        id: categoryId,
        name: category?.name || t('tasks:calendar.unknown_category'),
        tasks: categoryTasks.map(task => {
          const startDate = task.scheduled_date 
            ? new Date(task.scheduled_date) 
            : new Date(task.created_at);
          
          const endDate = task.due_date 
            ? new Date(task.due_date) 
            : new Date(startDate.getTime() + (7 * 24 * 60 * 60 * 1000));

          return {
            id: task.task_id,
            name: task.title,
            startDate: startDate,
            endDate: endDate,
            color: getPriorityColor(task.priority),
            priority: task.priority,
            state: task.state,
            assignee: task.primary_assignee?.name,
            description: task.description,
            is_recurring: task.next_task_id === null ? task.is_recurring : null,
            rrule: task.next_task_id === null ? task.rrule : null,
            originalTask: task,
          };
        })
      };
    }).filter(category => category.tasks.length > 0);

    return {
      categories: ganttCategories,
      baseDate: currentDate,
    };
  }, [calendarTasks, categories, currentDate, t]);

  const handleTaskClick = useCallback((task) => {
    const taskId = task.task_id || task.originalTask?.task_id;
    if (taskId) {
      navigate(`/tasks/${taskId}`);
    }
  }, [navigate]);

  return (
    <Box>
      <PageHeader
        icon={CalendarMonthOutlinedIcon}
        title={t('tasks:calendar.title')}
        subtitle={t('tasks:calendar.subtitle')}
      />
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Gantt Chart */}
      <Box sx={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}>
        {ganttData.categories.length > 0 ? (
          <GanttChart
            showHeader={false}
            categories={ganttData.categories}
            baseDate={ganttData.baseDate}
            onTaskClick={handleTaskClick}
            onDateRangeChange={handleDateRangeChange}
            enableDnd={false}
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="h6" color="text.secondary">
              {loading ? t('tasks:calendar.loading') : t('tasks:calendar.no_tasks')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}